import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle, Smartphone, Mail, Shield, RefreshCw, Copy, Check, Trash2, AlertTriangle, Bug } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const MfaSettings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isMandatorySetup = user?.requiresMfaSetup === true;
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const needsLoginMfaActivation = mfaStatus?.totpConfigured && !mfaStatus?.loginMfaActive;
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  useEffect(() => {
    if (isMandatorySetup && mfaStatus && !mfaStatus.totpConfigured && !qrCode && !loading) {
      handleSetupTotp();
    }
  }, [isMandatorySetup, mfaStatus, qrCode, loading]);

  const handleActivateLoginMfa = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod');
      return;
    }
    try {
      setVerifying(true);
      const response = await api.post('/mfa/totp/activate-login', { code: verificationCode });
      if (response.data.success) {
        toast.success('Logowanie dwuskładnikowe zostało aktywowane');
        setVerificationCode('');
        await fetchMfaStatus();
        const updatedUser = await refreshUser();
        if (updatedUser?.requiresMfaSetup === false) {
          navigate('/app/invoices/list');
        }
      } else {
        toast.error(response.data.error || 'Nieprawidłowy kod');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas aktywacji logowania 2FA');
    } finally {
      setVerifying(false);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mfa/status');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Error fetching MFA status:', error);
      toast.error('Błąd podczas pobierania statusu MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupTotp = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mfa/totp/setup');
      setQrCode(response.data.qrCode);
      setSecret(response.data.manualEntryKey);
      toast.success('Sekret TOTP został wygenerowany');
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      toast.error(error.response?.data?.error || 'Błąd podczas konfiguracji TOTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod');
      return;
    }

    try {
      setVerifying(true);
      const response = await api.post('/mfa/totp/verify', {
        code: verificationCode
      });

      if (response.data.success) {
        toast.success('Uwierzytelnianie dwuskładnikowe zostało aktywowane');
        setQrCode(null);
        setSecret(null);
        setVerificationCode('');
        setDebugInfo(null);
        await fetchMfaStatus();
        const updatedUser = await refreshUser();
        if (updatedUser?.requiresMfaSetup === false) {
          navigate('/app/invoices/list');
        }
      } else {
        // Pokaż informacje debugowe jeśli są dostępne
        if (response.data?.debug) {
          const debugData = {
            ...response.data.debug,
            providedCode: response.data.providedCode || verificationCode
          };
          setDebugInfo(debugData);
          toast.error(`Nieprawidłowy kod. Wprowadzony: ${debugData.providedCode}. Aktualny kod serwera: ${debugData.currentCode}. Sprawdź panel debugowy poniżej.`, {
            duration: 5000
          });
        } else {
          toast.error(response.data?.error || 'Nieprawidłowy kod');
          setDebugInfo(null);
        }
      }
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      console.error('Error response:', error.response?.data);
      
      // Pokaż informacje debugowe jeśli są dostępne
      if (error.response?.data?.debug) {
        const debugData = {
          ...error.response.data.debug,
          providedCode: error.response.data.providedCode || verificationCode
        };
        setDebugInfo(debugData);
        toast.error(`Błąd: ${error.response?.data?.error || 'Nieprawidłowy kod'}. Sprawdź panel debugowy poniżej.`, {
          duration: 5000
        });
      } else {
        toast.error(error.response?.data?.error || 'Błąd podczas weryfikacji kodu');
        setDebugInfo(null);
      }
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Skopiowano do schowka');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchDiagnosticInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/mfa/totp/diagnostic');
      setDiagnosticData(response.data);
      setShowDiagnostic(true);
      toast.success('Pobrano informacje diagnostyczne');
    } catch (error) {
      console.error('Error fetching diagnostic info:', error);
      toast.error(error.response?.data?.error || 'Błąd podczas pobierania informacji diagnostycznych');
    } finally {
      setLoading(false);
    }
  };

  const handleReconfigureTotp = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Wprowadź aktualny 6-cyfrowy kod z Authenticatora');
      return;
    }
    try {
      setDisabling(true);
      const response = await api.post('/mfa/totp/reconfigure', { code: verificationCode });

      if (response.data.success) {
        toast.success(response.data.message || 'Stary TOTP został usunięty. Zeskanuj nowy kod QR.');
        setQrCode(response.data.qrCode);
        setSecret(response.data.manualEntryKey);
        setShowDisableConfirm(false);
        setVerificationCode('');
        setDebugInfo(null);
        setMfaStatus((prev) => prev ? {
          ...prev,
          loginMfaActive: false,
          totpConfigured: false,
          totpEnabled: false
        } : prev);
        await fetchMfaStatus();
        await refreshUser();
      } else {
        toast.error(response.data.error || 'Nie udało się wymienić TOTP');
      }
    } catch (error) {
      console.error('Error reconfiguring TOTP:', error);
      toast.error(error.response?.data?.error || 'Błąd podczas wymiany TOTP');
    } finally {
      setDisabling(false);
    }
  };

  if (loading && !mfaStatus) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="card">
      {isMandatorySetup && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900">Wymagana konfiguracja bezpieczeństwa</h3>
              <p className="text-sm text-amber-800 mt-1">
                Aby korzystać z systemu CRM, musisz skonfigurować logowanie dwuskładnikowe (TOTP).
                Dopóki nie aktywujesz aplikacji Authenticator, dostęp do pozostałych funkcji jest zablokowany.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="card-header">
        <h2 className="text-xl">
          {isMandatorySetup ? 'Konfiguracja logowania dwuskładnikowego' : 'Konfiguracja podpisu elektronicznego'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {isMandatorySetup
            ? 'Ten sam kod TOTP służy do logowania do CRM oraz podpisywania dokumentów'
            : 'Skonfiguruj dwuskładnikowe uwierzytelnianie (MFA) do podpisywania dokumentów i logowania'}
        </p>
      </div>
      <div className="card-body">
        {/* Status MFA */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status uwierzytelniania</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* TOTP Status */}
            <div className={`p-4 border-2 rounded-lg ${
              mfaStatus?.totpEnabled && mfaStatus?.totpVerified
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Key className={`h-5 w-5 mr-2 ${
                    mfaStatus?.totpEnabled && mfaStatus?.totpVerified
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`} />
                  <span className="font-medium text-gray-900">TOTP</span>
                </div>
                {mfaStatus?.totpEnabled && mfaStatus?.totpVerified ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <p className="text-sm text-gray-600">
                {mfaStatus?.totpEnabled && mfaStatus?.totpVerified
                  ? 'Aktywne'
                  : 'Nieaktywne'}
              </p>
            </div>

            {/* SMS Status */}
            <div className="p-4 border-2 border-gray-200 bg-gray-50 rounded-lg opacity-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Smartphone className="h-5 w-5 mr-2 text-gray-400" />
                  <span className="font-medium text-gray-900">SMS</span>
                </div>
                <XCircle className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">Wkrótce</p>
            </div>

            {/* Email Status */}
            <div className="p-4 border-2 border-gray-200 bg-gray-50 rounded-lg opacity-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-gray-400" />
                  <span className="font-medium text-gray-900">Email</span>
                </div>
                <XCircle className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">Wkrótce</p>
            </div>
          </div>
        </div>

            {/* Konfiguracja TOTP */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Konfiguracja TOTP</h3>
                {mfaStatus?.totpEnabled && (
                  <button
                    onClick={fetchDiagnosticInfo}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center"
                  >
                    <Bug className="h-4 w-4 mr-1" />
                    Informacje diagnostyczne
                  </button>
                )}
              </div>
          
          {mfaStatus?.loginMfaActive ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      TOTP jest aktywne
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Możesz logować się do CRM oraz podpisywać dokumenty kodem z aplikacji Authenticator
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setVerificationCode('');
                    setShowDisableConfirm(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Usuń i skonfiguruj nowy TOTP
                </button>
              </div>
            </div>
          ) : needsLoginMfaActivation ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900 font-medium mb-2">Aktywuj logowanie dwuskładnikowe</p>
                <p className="text-sm text-blue-800">
                  Masz już skonfigurowany TOTP do podpisywania dokumentów. Wprowadź bieżący kod z aplikacji Authenticator,
                  aby włączyć logowanie 2FA do panelu CRM.
                </p>
              </div>
              <div className="flex items-end space-x-3">
                <div className="flex-1">
                  <label htmlFor="activationCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Kod weryfikacyjny (6 cyfr)
                  </label>
                  <input
                    id="activationCode"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleActivateLoginMfa}
                  disabled={verifying || verificationCode.length !== 6}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {verifying ? 'Aktywuję...' : 'Aktywuj logowanie 2FA'}
                </button>
              </div>
              <button
                type="button"
                onClick={handleSetupTotp}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                Skonfiguruj TOTP od nowa (nowy kod QR)
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {!qrCode ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">
                        Jak skonfigurować TOTP?
                      </h4>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Kliknij przycisk "Konfiguruj TOTP" poniżej</li>
                        <li>Zeskanuj kod QR w aplikacji TOTP (Google Authenticator, Authy, Microsoft Authenticator lub innej)</li>
                        <li>Lub wprowadź sekret ręcznie</li>
                        <li>Wprowadź kod weryfikacyjny z aplikacji</li>
                        <li>Gotowe! Możesz podpisywać dokumenty</li>
                      </ol>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">
                    Krok 1: Zeskanuj kod QR lub wprowadź sekret ręcznie
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* QR Code */}
                    <div className="flex flex-col items-center">
                      <div className="bg-white p-4 border-2 border-gray-200 rounded-lg mb-4">
                        <img 
                          src={qrCode} 
                          alt="QR Code TOTP" 
                          className="w-48 h-48"
                        />
                      </div>
                      <p className="text-xs text-gray-600 text-center">
                        Zeskanuj kod QR w aplikacji TOTP
                      </p>
                    </div>

                    {/* Manual Entry */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sekret (do wprowadzenia ręcznego)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={secret}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(secret)}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                          title="Kopiuj sekret"
                        >
                          {copied ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <Copy className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        Jeśli nie możesz zeskanować QR kodu, wprowadź ten sekret ręcznie w aplikacji TOTP
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">
                      Krok 2: Wprowadź kod weryfikacyjny
                    </h4>
                    <div className="flex items-end space-x-3">
                      <div className="flex-1">
                        <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                          Kod weryfikacyjny (6 cyfr)
                        </label>
                        <input
                          id="verificationCode"
                          type="text"
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setVerificationCode(value);
                          }}
                          maxLength={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest font-mono"
                          placeholder="000000"
                          autoFocus
                        />
                      </div>
                      <button
                        onClick={handleVerifyTotp}
                        disabled={verifying || verificationCode.length !== 6}
                        className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {verifying ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Weryfikowanie...</span>
                          </>
                        ) : (
                          'Aktywuj TOTP'
                        )}
                      </button>
                    </div>
                    
                    {/* Informacje debugowe - ZAWSZE WIDOCZNE PO BŁĘDZIE */}
                    {debugInfo && (
                      <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                        <h5 className="text-sm font-bold text-yellow-900 mb-3 flex items-center">
                          <AlertTriangle className="h-5 w-5 mr-2" />
                          Informacje debugowe (tylko dla testów)
                        </h5>
                        <div className="text-xs text-yellow-800 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p><strong>Wprowadzony kod:</strong></p>
                              <p className="font-mono text-lg font-bold">{debugInfo.providedCode || verificationCode}</p>
                            </div>
                            <div>
                              <p><strong>Aktualny kod serwera:</strong></p>
                              <p className="font-mono text-lg font-bold text-green-700">{debugInfo.currentCode}</p>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-yellow-300">
                            <p className="font-semibold mb-2">Wszystkie kody serwera (±2 okna czasowe):</p>
                            <div className="grid grid-cols-5 gap-2">
                              {debugInfo.timeWindows && Object.entries(debugInfo.timeWindows).map(([offset, code]) => (
                                <div key={offset} className={`p-2 rounded text-center ${
                                  offset === '0' ? 'bg-yellow-200 font-bold' : 'bg-yellow-100'
                                }`}>
                                  <p className="text-xs text-yellow-700">Offset {offset}</p>
                                  <p className="font-mono font-bold">{code}</p>
                                </div>
                              ))}
                            </div>
                            {!debugInfo.timeWindows && (
                              <div className="space-y-1">
                                <p><strong>Poprzedni kod (-1):</strong> <span className="font-mono">{debugInfo.previousCode}</span></p>
                                <p><strong>Aktualny kod (0):</strong> <span className="font-mono font-bold text-green-700">{debugInfo.currentCode}</span></p>
                                <p><strong>Następny kod (+1):</strong> <span className="font-mono">{debugInfo.nextCode}</span></p>
                                {debugInfo.previous2Code && (
                                  <p><strong>Poprzedni kod (-2):</strong> <span className="font-mono">{debugInfo.previous2Code}</span></p>
                                )}
                                {debugInfo.next2Code && (
                                  <p><strong>Następny kod (+2):</strong> <span className="font-mono">{debugInfo.next2Code}</span></p>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-yellow-300 text-xs">
                            <p><strong>Epoch Second:</strong> {debugInfo.epochSecond}</p>
                            <p><strong>TimeStep:</strong> {debugInfo.currentTimeStep}</p>
                            <p><strong>Czas serwera:</strong> {debugInfo.serverTime ? new Date(debugInfo.serverTime).toLocaleString('pl-PL') : 'N/A'}</p>
                            {debugInfo.secretHash && (
                              <p><strong>Hash sekretu:</strong> <span className="font-mono text-xs">{debugInfo.secretHash}</span></p>
                            )}
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-yellow-300 bg-yellow-100 p-2 rounded">
                            <p className="font-semibold text-yellow-900 mb-1">Wskazówki:</p>
                            <ul className="list-disc list-inside space-y-1 text-yellow-800">
                              <li>Porównaj kod z telefonu z kodami powyżej</li>
                              <li>Jeśli kod pasuje do "Offset -1" lub "-2" → telefon ma czas przesunięty wstecz</li>
                              <li>Jeśli kod pasuje do "Offset +1" lub "+2" → telefon ma czas przesunięty w przód</li>
                              <li>Jeśli kod nie pasuje do żadnego → sprawdź czy sekret jest poprawny (porównaj hash)</li>
                              <li>Upewnij się, że czas w telefonie jest zsynchronizowany (automatyczna synchronizacja)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Panel diagnostyczny */}
                  {showDiagnostic && diagnosticData && (
                    <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-400 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-bold text-blue-900 flex items-center">
                          <Bug className="h-5 w-5 mr-2" />
                          Informacje diagnostyczne TOTP
                        </h5>
                        <button
                          onClick={() => setShowDiagnostic(false)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="text-xs text-blue-800 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p><strong>Epoch Second:</strong></p>
                            <p className="font-mono">{diagnosticData.epochSecond}</p>
                          </div>
                          <div>
                            <p><strong>TimeStep:</strong></p>
                            <p className="font-mono">{diagnosticData.currentTimeStep}</p>
                          </div>
                        </div>
                        <div>
                          <p><strong>Czas serwera:</strong></p>
                          <p className="font-mono">{diagnosticData.serverTimeISO || new Date(diagnosticData.serverTime).toLocaleString('pl-PL')}</p>
                        </div>
                        {diagnosticData.secretHash && (
                          <div>
                            <p><strong>Hash sekretu:</strong></p>
                            <p className="font-mono text-xs break-all">{diagnosticData.secretHash}</p>
                          </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-blue-300">
                          <p className="font-semibold mb-2">Kody TOTP dla wszystkich okien czasowych:</p>
                          <div className="grid grid-cols-5 gap-2">
                            {diagnosticData.codes && Object.entries(diagnosticData.codes).map(([key, code]) => {
                              const isCurrent = key.includes('(offset 0)');
                              return (
                                <div key={key} className={`p-2 rounded text-center ${
                                  isCurrent ? 'bg-blue-200 font-bold' : 'bg-blue-100'
                                }`}>
                                  <p className="text-xs text-blue-700">{key.replace('timeStep_', '').replace(' (offset 0)', '')}</p>
                                  <p className="font-mono font-bold">{code}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <button
                      onClick={() => {
                        setQrCode(null);
                        setSecret(null);
                        setVerificationCode('');
                        setDebugInfo(null);
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Wygeneruj nowy kod QR
                    </button>
                  </div>
                </div>
              )}

              {!qrCode && (
                <div className="flex justify-end">
                  <button
                    onClick={handleSetupTotp}
                    disabled={loading}
                    className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Generowanie...</span>
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Konfiguruj TOTP
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Informacje o aplikacjach TOTP */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Zalecane aplikacje TOTP</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-1">Google Authenticator</h4>
              <p className="text-xs text-gray-600">iOS, Android</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-1">Authy</h4>
              <p className="text-xs text-gray-600">iOS, Android, Desktop</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-1">Microsoft Authenticator</h4>
              <p className="text-xs text-gray-600">iOS, Android</p>
            </div>
          </div>
          
          {/* Informacje o bezpieczeństwie */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  Ważne informacje o bezpieczeństwie
                </h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Nie udostępniaj sekretu TOTP innym osobom</li>
                  <li>Nie udostępniaj kodów weryfikacyjnych - są jednorazowe i ważne tylko przez 30 sekund</li>
                  <li>Zabezpiecz telefon hasłem/biometrią</li>
                  <li>W przypadku utraty telefonu Super Admin może zresetować TOTP, a przy dostępie do aplikacji możesz sam wymienić TOTP na nowy</li>
                  <li>Kody TOTP są wymagane przy każdym logowaniu do CRM oraz podpisywaniu dokumentów</li>
                  <li>Całkowite wyłączenie uwierzytelniania dwuskładnikowego nie jest możliwe — można tylko usunąć stary TOTP i dodać nowy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {showDisableConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-6">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Usuń TOTP i dodaj nowy</h3>
              <p className="text-sm text-gray-600 mt-2">
                TOTP nadal będzie wymagany do logowania. Po potwierdzeniu aktualnym kodem stary sekret
                zostanie usunięty i pojawi się nowy kod QR do zeskanowania w Authenticatorze.
              </p>
            </div>
          </div>
          <label htmlFor="reconfigureCode" className="block text-sm font-medium text-gray-700 mb-2">
            Aktualny kod z Authenticatora (6 cyfr)
          </label>
          <input
            id="reconfigureCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-2xl tracking-widest font-mono mb-4"
            placeholder="000000"
            autoFocus
          />
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowDisableConfirm(false);
                setVerificationCode('');
              }}
              disabled={disabling}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={handleReconfigureTotp}
              disabled={disabling || verificationCode.length !== 6}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {disabling ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Wymiana...</span>
                </>
              ) : (
                'Usuń i dodaj nowy'
              )}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default MfaSettings;
